import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { api } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import { Loader2, Calculator, Save, AlertCircle, CheckCircle2 } from 'lucide-react';
import { formatCurrency, formatDate } from '@/utils/helpers';

const InterestDistribution = () => {
    const [loading, setLoading] = useState(false);
    const [distributing, setDistributing] = useState(false);
    const [data, setData] = useState(null);

    // Default to current month/year
    const today = new Date();
    const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
    const [year, setYear] = useState(today.getFullYear());

    const [overrideAmount, setOverrideAmount] = useState('');
    const [isEditing, setIsEditing] = useState(false);

    const fetchData = async () => {
        try {
            setLoading(true);
            const result = await api.interest.calculate(month, year);
            setData(result);
            setOverrideAmount(result.total_interest.toString());
            setIsEditing(false); // Reset edit mode on fetch
        } catch (error) {
            console.error("Failed to fetch interest data", error);
            toast.error("Failed to calculate interest");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [month, year]);

    const handleDistribute = async () => {
        if (!data) return;

        // Use override amount if valid, else calculated
        const totalToDistribute = parseFloat(overrideAmount);

        if (isNaN(totalToDistribute) || totalToDistribute <= 0) {
            toast.error("Invalid distribution amount");
            return;
        }

        try {
            setDistributing(true);
            await api.interest.distribute({
                month,
                year,
                total_amount: totalToDistribute,
                mandali_id: 1 // TODO: Get from user context if super admin
            });

            toast.success("Interest distributed successfully!");
            fetchData(); // Refresh
        } catch (error) {
            console.error("Distribution failed", error);
            toast.error(error.response?.data?.detail || "Failed to distribute interest");
        } finally {
            setDistributing(false);
        }
    };

    // Helper for generating year options (current - 2 to current + 2)
    const yearOptions = Array.from({ length: 5 }, (_, i) => today.getFullYear() - 2 + i);

    return (
        <DashboardLayout>
            <div className="space-y-6 fade-in pt-2">
                {/* Header - Consistent spacing */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-2">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900">Interest Distribution</h1>
                        <p className="text-sm text-gray-500">Calculate and distribute monthly loan interest to members.</p>
                    </div>

                    <div className="flex items-center gap-2">
                        <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                            <SelectTrigger className="w-[120px] sm:w-[140px]">
                                <SelectValue placeholder="Month" />
                            </SelectTrigger>
                            <SelectContent>
                                {Array.from({ length: 12 }, (_, i) => (
                                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                                        {new Date(0, i).toLocaleString('default', { month: 'long' })}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                            <SelectTrigger className="w-[80px] sm:w-[100px]">
                                <SelectValue placeholder="Year" />
                            </SelectTrigger>
                            <SelectContent>
                                {yearOptions.map(y => (
                                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Button variant="outline" size="icon" onClick={fetchData} title="Refresh Calculation">
                            <Calculator size={18} />
                        </Button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center p-12">
                        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                    </div>
                ) : data ? (
                    <>
                        {/* Summary Cards */}
                        <div className="grid gap-4 md:grid-cols-3">
                            <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-medium text-emerald-800">Total Interest Collected</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center justify-between">
                                        {isEditing ? (
                                            <div className="flex items-center gap-2">
                                                <Input
                                                    type="number"
                                                    value={overrideAmount}
                                                    onChange={(e) => setOverrideAmount(e.target.value)}
                                                    className="h-9 w-32 bg-white"
                                                />
                                                <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)}>
                                                    <CheckCircle2 size={18} className="text-emerald-600" />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <div className="text-lg md:text-2xl font-bold text-gray-900">
                                                    {formatCurrency(parseFloat(overrideAmount) || 0)}
                                                </div>
                                                <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)} className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600">
                                                    <span className="sr-only">Edit</span>
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                                        <path d="M5.433 13.917l1.262-3.155A4 4 0 017.58 9.42l6.92-6.918a2.121 2.121 0 013 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 01-.65-.65z" />
                                                        <path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0010 3H4.75A2.75 2.75 0 002 5.75v9.5A2.75 2.75 0 004.75 18h9.5A2.75 2.75 0 0017 15.25V10a.75.75 0 00-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5z" />
                                                    </svg>
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-xs text-emerald-600 mt-1">
                                        Based on {data.loans.length} new loans
                                    </p>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-medium text-gray-500">Eligible Members</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-lg md:text-2xl font-bold text-gray-900">{data.eligible_members_count}</div>
                                    <p className="text-xs text-gray-500 mt-1">Active account holders</p>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-medium text-gray-500">Share Per Member</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-lg md:text-2xl font-bold text-emerald-600">
                                        {formatCurrency((parseFloat(overrideAmount) || 0) / (data.eligible_members_count || 1))}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">Estimated profit share</p>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Action Bar */}
                        <div className="flex justify-end bg-yellow-50 p-4 rounded-lg border border-yellow-100 items-center gap-4">
                            <div className="flex items-center gap-2 text-yellow-800 text-sm">
                                <AlertCircle size={16} />
                                <span>Distribution creates permanent financial records.</span>
                            </div>
                            <Button
                                onClick={handleDistribute}
                                disabled={distributing || parseFloat(overrideAmount) <= 0}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                                {distributing ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Distributing...
                                    </>
                                ) : (
                                    <>
                                        <Save className="mr-2 h-4 w-4" />
                                        Approve & Distribute
                                    </>
                                )}
                            </Button>
                        </div>

                        {/* Loans Table */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg md:text-xl">Loans Disbursed in {new Date(0, month - 1).toLocaleString('default', { month: 'long' })} {year}</CardTitle>
                                <p className="text-xs text-muted-foreground md:hidden mt-1">← Scroll horizontally to see all columns →</p>
                            </CardHeader>
                            <CardContent className="p-0 sm:p-6">
                                <div className="overflow-x-auto table-scroll-container">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="whitespace-nowrap">Date</TableHead>
                                            <TableHead className="whitespace-nowrap">Loan A/C</TableHead>
                                            <TableHead className="whitespace-nowrap">Member</TableHead>
                                            <TableHead className="text-right whitespace-nowrap">Principal</TableHead>
                                            <TableHead className="text-right whitespace-nowrap">Tenure</TableHead>
                                            <TableHead className="text-right whitespace-nowrap">Interest (0.5%)</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {data.loans.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                                                    No loans found for this period
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            data.loans.map((loan) => (
                                                <TableRow key={loan.loan_id}>
                                                    <TableCell>{formatDate(loan.loan_application_date)}</TableCell>
                                                    <TableCell className="font-medium">{loan.loan_account_number}</TableCell>
                                                    <TableCell>{loan.member_name}</TableCell>
                                                    <TableCell className="text-right">{formatCurrency(loan.principal_amount)}</TableCell>
                                                    <TableCell className="text-right">{loan.tenure_months} Mo</TableCell>
                                                    <TableCell className="text-right font-medium text-emerald-600">
                                                        {formatCurrency(loan.interest_amount)}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                                </div>
                            </CardContent>
                        </Card>
                    </>
                ) : null}
            </div>
        </DashboardLayout>
    );
};

export default InterestDistribution;
